/**
 * กิลด์ and ศึกกิลด์ (systems/guild.ts, systems/guildWar.ts): what a guild costs, its ranks, how it grows, the nightly
 * war's clock, scoring and rewards, the simulated guilds of this single-player build, and the battlefield
 * สมรภูมิธงสามผืน as layout data. Pure data and pure functions; a server runs the same rules later
 * (docs/ONLINE.md §5, "guild v1").
 */
import { ARENA_ROSTER } from './arena';

export const GUILD = {
  /** Founding a guild at the registrar (ตำลึง). */
  foundCost: 5000,
  /** Longest guild name (characters). */
  nameMax: 12,
  /** Longest guild notice (characters). */
  noticeMax: 64,
  /** Longest guild chat line (characters). */
  chatMax: 80,
  /** Smallest donation to the coffer (ตำลึง). */
  donateMin: 100,
  /** Contribution earned per ตำลึง donated. */
  contributionPerGold: 1,
} as const;

/** The emblem colours a guild may choose. */
export const GUILD_COLORS = ['#b3202a', '#1f4e8c', '#2f7d4a', '#c79a2b', '#5b2a6e', '#2b2b2b'] as const;

export type GuildRankId = 'leader' | 'deputy' | 'elder' | 'member';

export interface GuildRankDef {
  readonly id: GuildRankId;
  readonly name: string;
  /** How many may hold it (the rest are สมาชิก). */
  readonly max: number;
  readonly canInvite: boolean;
  readonly canKick: boolean;
  readonly canNotice: boolean;
  /** May set others' ranks (only ranks below their own). */
  readonly canPromote: boolean;
}

/** Highest first: a rank may act only on those below it. */
export const GUILD_RANKS: readonly GuildRankDef[] = [
  { id: 'leader', name: 'เจ้ากิลด์', max: 1, canInvite: true, canKick: true, canNotice: true, canPromote: true },
  { id: 'deputy', name: 'รองเจ้ากิลด์', max: 2, canInvite: true, canKick: true, canNotice: true, canPromote: false },
  { id: 'elder', name: 'ผู้อาวุโส', max: 5, canInvite: true, canKick: false, canNotice: false, canPromote: false },
  { id: 'member', name: 'สมาชิก', max: Infinity, canInvite: false, canKick: false, canNotice: false, canPromote: false },
];

export const guildRank = (id: GuildRankId): GuildRankDef => GUILD_RANKS.find((r) => r.id === id)!;
/** 0 for เจ้ากิลด์ … 3 for สมาชิก: a smaller number outranks a larger one. */
export const rankOrder = (id: GuildRankId): number => GUILD_RANKS.findIndex((r) => r.id === id);
export const isGuildRank = (v: unknown): v is GuildRankId => GUILD_RANKS.some((r) => r.id === v);

export interface GuildLevelDef {
  readonly level: number;
  /** Total guild contribution needed to reach this level. */
  readonly contribution: number;
  /** Member cap at this level. */
  readonly cap: number;
  /** Passive buff for every member: qi and พลังชีวิต regeneration multiplier (systems/guild.ts `regenMult`). */
  readonly regen: number;
  /** Bonus on ตำลึง and แต้มฝึกปรือ from the war (fraction). */
  readonly warBonus: number;
  /** What the buff says in the guild window. */
  readonly label: string;
}

export const GUILD_LEVELS: readonly GuildLevelDef[] = [
  { level: 1, contribution: 0, cap: 30, regen: 1, warBonus: 0, label: 'ยังไม่มีพลังกิลด์' },
  { level: 2, contribution: 10_000, cap: 35, regen: 1.02, warBonus: 0.05, label: 'ฟื้นฟูพลังชีวิตและลมปราณ +2%' },
  { level: 3, contribution: 30_000, cap: 40, regen: 1.04, warBonus: 0.1, label: 'ฟื้นฟูพลังชีวิตและลมปราณ +4%' },
  { level: 4, contribution: 70_000, cap: 45, regen: 1.06, warBonus: 0.15, label: 'ฟื้นฟูพลังชีวิตและลมปราณ +6%' },
  { level: 5, contribution: 150_000, cap: 50, regen: 1.08, warBonus: 0.2, label: 'ฟื้นฟูพลังชีวิตและลมปราณ +8%' },
];

/** The guild level a total contribution reaches. */
export function guildLevelFor(contribution: number): GuildLevelDef {
  let lv = GUILD_LEVELS[0]!;
  for (const l of GUILD_LEVELS) if (contribution >= l.contribution) lv = l;
  return lv;
}

// ───────────────────────── simulated guilds ─────────────────────────

/** A simulated member: an arena contender (data/arena.ts) standing in for another player. */
export interface SimMember {
  readonly name: string;
  readonly rank: GuildRankId;
  /** Fighting strength in the war's simple duel model (from the contender's build). */
  readonly power: number;
}

export interface SimGuildDef {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly notice: string;
  readonly contribution: number;
  readonly members: readonly SimMember[];
}

/** A contender's strength: the art's level and the neigong's, as the arena ranks them. */
export const rosterPower = (i: number): number => {
  const b = ARENA_ROSTER[i]!;
  return b.skillLevel * 10 + b.neigongLevel * 5;
};

const simMembers = (from: number, count: number): SimMember[] =>
  Array.from({ length: count }, (_, k) => {
    const i = from + k;
    const rank: GuildRankId = k === 0 ? 'leader' : k <= 2 ? 'deputy' : k <= 5 ? 'elder' : 'member';
    return { name: ARENA_ROSTER[i]!.name, rank, power: rosterPower(i) };
  });

/** Contenders 0–35 are spread over three guilds; 36 onward are free to be invited. */
export const SIM_GUILDS: readonly SimGuildDef[] = [
  {
    id: 'jade-lotus',
    name: 'บัวหยก',
    color: GUILD_COLORS[2],
    notice: 'ศึกกิลด์คืนนี้ สองทุ่มตรง อย่ามาสาย',
    contribution: 32_000,
    members: simMembers(0, 12),
  },
  {
    id: 'black-tiger',
    name: 'พยัคฆ์ทมิฬ',
    color: GUILD_COLORS[5],
    notice: 'ผู้อ่อนแอไม่ต้องมา',
    contribution: 18_000,
    members: simMembers(12, 12),
  },
  {
    id: 'azure-cloud',
    name: 'เมฆาคราม',
    color: GUILD_COLORS[1],
    notice: 'ยินดีต้อนรับสหายใหม่',
    contribution: 6_000,
    members: simMembers(24, 12),
  },
];

/** Contenders not in any simulated guild: who answers a player guild's invitation, in this order. */
export const FREE_RECRUITS: readonly SimMember[] = ARENA_ROSTER.slice(36).map((b, k) => ({
  name: b.name,
  rank: 'member',
  power: rosterPower(36 + k),
}));

export const simGuild = (id: string): SimGuildDef | undefined => SIM_GUILDS.find((g) => g.id === id);

// ───────────────────────── the war ─────────────────────────

/**
 * Who a guild fights in the war (the user's rule: every activity offers NPCs or players). `npc`: the player's guild
 * against 1–2 AI guilds of simulated members, played offline now. `pvp`: against other players' guilds, matched by
 * the server; with no player guild signed up it falls back to `npc`.
 */
export type GuildWarMode = 'npc' | 'pvp';
export const GUILD_WAR_MODES: Readonly<Record<GuildWarMode, { readonly label: string }>> = {
  npc: { label: 'สู้กับ NPC' },
  pvp: { label: 'สู้กับผู้เล่น' },
};
export const isGuildWarMode = (v: unknown): v is GuildWarMode => v === 'npc' || v === 'pvp';

/** Minutes of the day (0–1440). */
const hm = (h: number, m = 0) => h * 60 + m;

export const GUILD_WAR = {
  /** The war runs every day from `start` to `end` (minutes of the day). */
  start: hm(20),
  end: hm(20, 30),
  /** Sign-up closes at this minute (and opens again once the war is over). */
  signupClose: hm(19, 50),
  /** The reminder toast. */
  reminder: hm(19, 45),
  /** Seconds a defeated fighter waits at the camp. */
  respawn: 10,
  /** Metres from a banner that count as standing beside it. */
  captureRadius: 4,
  /** Seconds one side alone must stand by a banner to take it. */
  captureTime: 5,
  /** Points a side earns per second for each banner it holds. */
  pointsPerSecond: 1,
  /** Points per defeat. */
  pointsPerDefeat: 5,
  /** Fighters per side. */
  maxPerSide: 12,
  /** AI guilds in an `npc` war: at least, at most. */
  aiGuilds: [1, 2] as const,
  /** Metres a second a simulated fighter runs. */
  runSpeed: 5,
  /** Metres within which two fighters of different sides fight. */
  fightRange: 2.5,
  /** Chance per second that an even duel ends in a defeat (split by strength). */
  duelRate: 0.3,
  /** A simulated fighter's strength (0–1) lost by winning a duel; under `retreatHp` it goes back to its camp. */
  wear: 0.35,
  retreatHp: 0.3,
  /** Strength a second regained standing at its own camp (within `campReach` m). */
  campHeal: 0.15,
  campReach: 3,
} as const;

export interface GuildWarReward {
  readonly gold: number;
  readonly cultivation: number;
}

export const GUILD_WAR_REWARDS = {
  /** The winning guild: into the coffer, and contribution. */
  winner: { coffer: 3000, contribution: 500 },
  /** The winning guild's title for the day. */
  title: 'เจ้าสมรภูมิ',
  /** Every fighter, win or lose (ตำลึง and แต้มฝึกปรือ). */
  fighter: { gold: 300, cultivation: 200 } as GuildWarReward,
  /** A fighter of the winning guild, on top. */
  winnerFighter: { gold: 200, cultivation: 150 } as GuildWarReward,
} as const;

export type GuildWarPhase = 'signup' | 'closed' | 'war';

/** Where the day's war schedule stands at a minute of the day: sign-up, closed (19:50–20:00) or the war. */
export function guildWarPhase(minute: number): GuildWarPhase {
  const m = ((minute % 1440) + 1440) % 1440;
  if (m >= GUILD_WAR.start && m < GUILD_WAR.end) return 'war';
  if (m >= GUILD_WAR.signupClose && m < GUILD_WAR.start) return 'closed';
  return 'signup';
}

// ───────────────────────── the battlefield ─────────────────────────

export interface FieldPoint {
  readonly x: number;
  readonly y: number;
}
export interface FieldBanner extends FieldPoint {
  readonly id: string;
  readonly name: string;
}

/** สมรภูมิธงสามผืน: its own instance, metres from its north-west corner; three banners, a camp per side. */
export const GUILD_FIELD = {
  name: 'สมรภูมิธงสามผืน',
  width: 120,
  height: 90,
  banners: [
    { id: 'dragon', name: 'ธงมังกร', x: 30, y: 45 },
    { id: 'tiger', name: 'ธงพยัคฆ์', x: 60, y: 30 },
    { id: 'phoenix', name: 'ธงหงส์', x: 90, y: 45 },
  ] as readonly FieldBanner[],
  /** Camps by side; a two-guild war uses the first two. */
  camps: [
    { x: 10, y: 80 },
    { x: 110, y: 80 },
    { x: 60, y: 6 },
  ] as readonly FieldPoint[],
} as const;

/** The registrar (นายทะเบียนกิลด์): founding and joining are done by talking to one, like the smith's forge. */
export const GUILD_REGISTRAR_TITLE = 'นายทะเบียนกิลด์';
/** How near a registrar (metres) the player must stand to found or join a guild. */
export const GUILD_REGISTRAR_REACH = 5;
export interface GuildRegistrarDef {
  readonly zone: string;
  readonly name: string;
  readonly sprite: string;
  readonly at: string;
  readonly dx: number;
  readonly dy: number;
  readonly facing?: 1 | -1;
}
export const GUILD_REGISTRARS: readonly GuildRegistrarDef[] = [
  // The new Yanyu's town hall, on the plaza across from the smithy corner (data/smiths.ts).
  { zone: 'yanyu2', name: 'นายทะเบียนหลี่', sprite: 'farmer', at: 'plaza', dx: -7, dy: -20, facing: 1 },
];
